import React, { useState, useEffect } from 'react';
import {
    FaTimes,
    FaUser,
    FaEnvelope,
    FaMapMarkerAlt,
    FaPlus,
    FaCheckCircle,
    FaTags,
    FaSearch
} from 'react-icons/fa';
import styles from './AddContactModal.module.css';
import { contactService, ContactHelper } from '../../services/contactService';
import { contactTypeTagsService } from '../../services/contactTypeTagsService';
import { useAccessToken, useLockBodyScroll } from '../../hooks';
import { useAlert } from '../../context/AlertContext';
import { useContactTypes } from '../../hooks/useContactTypes';

/**
 * U.S. States lookup for dropdown
 */
const US_STATES = [
    { code: 'AL', name: 'Alabama' },
    { code: 'AK', name: 'Alaska' },
    { code: 'AZ', name: 'Arizona' },
    { code: 'AR', name: 'Arkansas' },
    { code: 'CA', name: 'California' },
    { code: 'CO', name: 'Colorado' },
    { code: 'CT', name: 'Connecticut' },
    { code: 'DE', name: 'Delaware' },
    { code: 'FL', name: 'Florida' },
    { code: 'GA', name: 'Georgia' },
    { code: 'HI', name: 'Hawaii' },
    { code: 'ID', name: 'Idaho' },
    { code: 'IL', name: 'Illinois' },
    { code: 'IN', name: 'Indiana' },
    { code: 'IA', name: 'Iowa' },
    { code: 'KS', name: 'Kansas' },
    { code: 'KY', name: 'Kentucky' },
    { code: 'LA', name: 'Louisiana' },
    { code: 'ME', name: 'Maine' },
    { code: 'MD', name: 'Maryland' },
    { code: 'MA', name: 'Massachusetts' },
    { code: 'MI', name: 'Michigan' },
    { code: 'MN', name: 'Minnesota' },
    { code: 'MS', name: 'Mississippi' },
    { code: 'MO', name: 'Missouri' },
    { code: 'MT', name: 'Montana' },
    { code: 'NE', name: 'Nebraska' },
    { code: 'NV', name: 'Nevada' },
    { code: 'NH', name: 'New Hampshire' },
    { code: 'NJ', name: 'New Jersey' },
    { code: 'NM', name: 'New Mexico' },
    { code: 'NY', name: 'New York' },
    { code: 'NC', name: 'North Carolina' },
    { code: 'ND', name: 'North Dakota' },
    { code: 'OH', name: 'Ohio' },
    { code: 'OK', name: 'Oklahoma' },
    { code: 'OR', name: 'Oregon' },
    { code: 'PA', name: 'Pennsylvania' },
    { code: 'RI', name: 'Rhode Island' },
    { code: 'SC', name: 'South Carolina' },
    { code: 'SD', name: 'South Dakota' },
    { code: 'TN', name: 'Tennessee' },
    { code: 'TX', name: 'Texas' },
    { code: 'UT', name: 'Utah' },
    { code: 'VT', name: 'Vermont' },
    { code: 'VA', name: 'Virginia' },
    { code: 'WA', name: 'Washington' },
    { code: 'WV', name: 'West Virginia' },
    { code: 'WI', name: 'Wisconsin' },
    { code: 'WY', name: 'Wyoming' }
];

/**
 * Add Contact Modal Component
 * Modal for creating a new contact and assigning contact types per client
 * OR searching for existing contact to link to client
 */
const AddContactModal = ({ clientId, clientName, clientCode, isOpen, onClose, onContactAdded }) => {
    const { getBackendAccessToken } = useAccessToken();
    const { setInfoAlert, setErrorAlert } = useAlert();
    const { contactTypes, loading: loadingContactTypes, loadContactTypes } = useContactTypes();

    // Lock body scroll when modal is open
    useLockBodyScroll(isOpen);

    // Mode state: 'search' | 'create' (default to search)
    const [mode, setMode] = useState('search');

    // Search state
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [selectedExistingContact, setSelectedExistingContact] = useState(null);

    // Form state
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        address1: '',
        address2: '',
        city: '',
        stateCode: '',
        zip: '',
        isSubscribed: true,
        clientPortalAccess: false,
        isActive: true
    });

    // Contact types selection
    const [selectedContactTypes, setSelectedContactTypes] = useState([]);

    // UI state
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});
    const [step, setStep] = useState(1); // 1: Contact Info, 2: Contact Types

    /**
     * Reset form when modal opens/closes
     */
    useEffect(() => {
        if (isOpen) {
            // Reset to search mode
            setMode('search');
            setSearchQuery('');
            setSearchResults([]);
            setSelectedExistingContact(null);
            setFormData({
                firstName: '',
                lastName: '',
                email: '',
                address1: '',
                address2: '',
                city: '',
                stateCode: '',
                zip: '',
                isSubscribed: true,
                clientPortalAccess: false,
                isActive: true
            });
            setSelectedContactTypes([]);
            setErrors({});
            setStep(1);
        }
    }, [isOpen]);

    /**
     * Handle form field changes
     */
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        // Clear field error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    /**
     * Handle contact type selection
     */
    const handleContactTypeToggle = (contactTypeId) => {
        setSelectedContactTypes(prev => {
            if (prev.includes(contactTypeId)) {
                return prev.filter(id => id !== contactTypeId);
            } else {
                return [...prev, contactTypeId];
            }
        });
    };

    /**
     * Handle search input change and perform search
     */
    const handleSearchChange = async (e) => {
        const query = e.target.value;
        setSearchQuery(query);

        if (query.trim().length < 2) {
            setSearchResults([]);
            return;
        }

        setSearching(true);
        try {
            const accessToken = await getBackendAccessToken();
            
            // Search by both first name and last name, combine results
            const [firstNameResults, lastNameResults] = await Promise.all([
                contactService.searchByFirstName(accessToken, query).catch(() => []),
                contactService.searchByLastName(accessToken, query).catch(() => [])
            ]);

            // Combine and deduplicate results
            const allResults = [...firstNameResults, ...lastNameResults];
            const uniqueResults = Array.from(
                new Map(allResults.map(contact => [contact.contactId, contact])).values()
            );

            setSearchResults(uniqueResults);
        } catch (error) {
            console.error('Search failed:', error);
            setErrorAlert('Failed to search contacts. Please try again.');
            setSearchResults([]);
        } finally {
            setSearching(false);
        }
    };

    /**
     * Handle selecting an existing contact
     */
    const handleSelectExistingContact = (contact) => {
        setSelectedExistingContact(contact);
    };

    /**
     * Handle linking existing contact to client
     */
    const handleLinkExistingContact = async () => {
        if (!selectedExistingContact || !clientId) return;

        // Load contact types if not already loaded
        if (contactTypes.length === 0) {
            await loadContactTypes();
        }

        // Move to contact types selection
        setMode('assign-types');
    };

    /**
     * Validate form data
     */
    const validateForm = () => {
        // Convert form data to DTO format (empty strings to null) before validation
        const dto = ContactHelper.createContactDto(formData);
        const validation = ContactHelper.validateContact(dto);
        
        if (!validation.valid) {
            const fieldErrors = {};
            validation.errors.forEach(error => {
                // Map error messages to field names
                if (error.includes('First name')) fieldErrors.firstName = error;
                else if (error.includes('Last name')) fieldErrors.lastName = error;
                else if (error.includes('Email')) fieldErrors.email = error;
                else if (error.includes('State code')) fieldErrors.stateCode = error;
                else if (error.includes('address1')) fieldErrors.address1 = error;
                else if (error.includes('address2')) fieldErrors.address2 = error;
                else if (error.includes('city')) fieldErrors.city = error;
                else if (error.includes('ZIP')) fieldErrors.zip = error;
            });
            setErrors(fieldErrors);
            return false;
        }

        setErrors({});
        return true;
    };

    /**
     * Handle next step (from contact info to contact types)
     */
    const handleNext = async () => {
        if (validateForm()) {
            // Load contact types when moving to step 2 (only if not already loaded)
            if (contactTypes.length === 0) {
                await loadContactTypes();
            }
            setStep(2);
        }
    };

    /**
     * Handle previous step
     */
    const handlePrevious = () => {
        setStep(1);
    };

    /**
     * Handle form submission for new contact
     */
    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate one more time
        if (!validateForm()) {
            setStep(1); // Go back to step 1 if validation fails
            return;
        }

        setSaving(true);

        try {
            const accessToken = await getBackendAccessToken();

            // Step 1: Create the contact
            const createdContact = await contactService.createContact(accessToken, formData);

            // Step 2: Assign contact types if any are selected
            if (selectedContactTypes.length > 0 && clientId) {
                await contactTypeTagsService.assignContactTypesToContact(
                    accessToken,
                    clientId,
                    createdContact.contactId,
                    selectedContactTypes
                );
            }

            // Success!
            setInfoAlert(
                `Contact "${ContactHelper.getFullName(createdContact)}" created successfully!`
            );

            // Call callback with created contact
            if (onContactAdded) {
                onContactAdded(createdContact);
            }

            // Close modal
            handleClose();
        } catch (error) {
            setErrorAlert(`Failed to create contact: ${error.message || 'Unknown error'}`);
        } finally {
            setSaving(false);
        }
    };

    /**
     * Handle assigning contact types to existing contact
     */
    const handleAssignContactTypes = async () => {
        if (!selectedExistingContact || !clientId) return;

        setSaving(true);

        try {
            const accessToken = await getBackendAccessToken();

            // Assign contact types if any are selected
            if (selectedContactTypes.length > 0) {
                await contactTypeTagsService.assignContactTypesToContact(
                    accessToken,
                    clientId,
                    selectedExistingContact.contactId,
                    selectedContactTypes
                );
            }

            // Success!
            setInfoAlert(
                `Contact "${ContactHelper.getFullName(selectedExistingContact)}" linked successfully!`
            );

            // Call callback
            if (onContactAdded) {
                onContactAdded(selectedExistingContact);
            }

            // Close modal
            handleClose();
        } catch (error) {
            setErrorAlert(`Failed to link contact: ${error.message || 'Unknown error'}`);
        } finally {
            setSaving(false);
        }
    };

    /**
     * Handle modal close
     */
    const handleClose = () => {
        setMode('search');
        setSearchQuery('');
        setSearchResults([]);
        setSelectedExistingContact(null);
        setFormData({
            firstName: '',
            lastName: '',
            email: '',
            address1: '',
            address2: '',
            city: '',
            stateCode: '',
            zip: '',
            isSubscribed: true,
            clientPortalAccess: false,
            isActive: true
        });
        setSelectedContactTypes([]);
        setErrors({});
        setStep(1);
        onClose();
    };

    // Don't render if not open
    if (!isOpen) return null;

    return (
        <div className={styles.modalOverlay}>
            <div className={styles.modalContent}>
                {/* Modal Header */}
                <div className={styles.modalHeader}>
                    <div className={styles.headerInfo}>
                        <div className={styles.headerIcon}>
                            <FaUser />
                        </div>
                        <div>
                            <h2 className={styles.modalTitle}>
                                {mode === 'search' && 'Add Contact to Client'}
                                {mode === 'create' && 'Create New Contact'}
                                {mode === 'assign-types' && 'Assign Contact Types'}
                            </h2>
                            <p className={styles.modalSubtitle}>
                                {clientCode && `${clientCode} - `}
                                {clientName || 'Unknown Client'}
                            </p>
                        </div>
                    </div>
                    <button
                        className={styles.closeButton}
                        onClick={handleClose}
                        title="Close"
                        disabled={saving}
                    >
                        <FaTimes />
                    </button>
                </div>

                {/* Search Mode: Search for existing contacts (with Add New button) */}
                {mode === 'search' && (
                    <div className={styles.searchContainer}>
                        <div className={styles.searchHeader}>
                            <div className={styles.searchInputWrapper}>
                                <FaSearch className={styles.searchIcon} />
                                <input
                                    type="text"
                                    className={styles.searchInput}
                                    placeholder="Search all contacts by first or last name..."
                                    value={searchQuery}
                                    onChange={handleSearchChange}
                                    autoFocus
                                />
                                {searchQuery && (
                                    <button
                                        className={styles.clearSearchButton}
                                        onClick={() => {
                                            setSearchQuery('');
                                            setSearchResults([]);
                                        }}
                                        title="Clear search"
                                    >
                                        <FaTimes />
                                    </button>
                                )}
                            </div>
                            <button
                                className={styles.addNewButton}
                                onClick={() => setMode('create')}
                                title="Create new contact"
                            >
                                <FaPlus /> Add New
                            </button>
                        </div>

                        {searching && (
                            <div className={styles.searchingState}>
                                <span className={styles.spinner}></span>
                                Searching...
                            </div>
                        )}

                        {!searching && !searchQuery.trim() && (
                            <div className={styles.emptyState}>
                                <FaSearch className={styles.emptyIcon} />
                                <p>Search for an existing contact by name</p>
                                <p className={styles.helpText}>
                                    Search across all contacts in the system, or click "Add New" to create a new contact
                                </p>
                            </div>
                        )}

                        {!searching && searchQuery.trim().length >= 2 && searchResults.length === 0 && (
                            <div className={styles.emptyState}>
                                <FaUser className={styles.emptyIcon} />
                                <p>No contacts found matching "{searchQuery}"</p>
                                <button
                                    className={styles.createNewButton}
                                    onClick={() => setMode('create')}
                                >
                                    <FaPlus /> Create New Contact
                                </button>
                            </div>
                        )}

                        {searchResults.length > 0 && (
                            <div className={styles.searchResultsContainer}>
                                <p className={styles.resultsCount}>
                                    Found {searchResults.length} contact{searchResults.length !== 1 ? 's' : ''}
                                </p>
                                <div className={styles.searchResultsList}>
                                    {searchResults.map(contact => (
                                        <div
                                            key={contact.contactId}
                                            className={`${styles.searchResultCard} ${
                                                selectedExistingContact?.contactId === contact.contactId
                                                    ? styles.selected
                                                    : ''
                                            }`}
                                            onClick={() => handleSelectExistingContact(contact)}
                                        >
                                            <div className={styles.contactResultInfo}>
                                                <h4>{ContactHelper.getFullName(contact)}</h4>
                                                {contact.email && (
                                                    <p className={styles.contactEmail}>
                                                        <FaEnvelope /> {contact.email}
                                                    </p>
                                                )}
                                                {(contact.address1 || contact.city) && (
                                                    <p className={styles.contactAddress}>
                                                        <FaMapMarkerAlt /> {ContactHelper.formatAddress(contact)}
                                                    </p>
                                                )}
                                                <div className={styles.contactStatus}>
                                                    {contact.isActive && (
                                                        <span className={styles.statusActive}>Active</span>
                                                    )}
                                                    {contact.isSubscribed && (
                                                        <span className={styles.statusSubscribed}>Subscribed</span>
                                                    )}
                                                    {contact.clientPortalAccess && (
                                                        <span className={styles.statusPortal}>Portal Access</span>
                                                    )}
                                                </div>
                                            </div>
                                            {selectedExistingContact?.contactId === contact.contactId && (
                                                <FaCheckCircle className={styles.selectedIcon} />
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {selectedExistingContact && (
                            <div className={styles.formActions}>
                                <button
                                    type="button"
                                    className={styles.nextButton}
                                    onClick={handleLinkExistingContact}
                                >
                                    Next: Contact Types →
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {/* Assign Types Mode: Assign contact types to existing contact */}
                {mode === 'assign-types' && selectedExistingContact && (
                    <div className={styles.formStep}>
                        <div className={styles.selectedContactSummary}>
                            <h4>Selected Contact</h4>
                            <p><strong>{ContactHelper.getFullName(selectedExistingContact)}</strong></p>
                            {selectedExistingContact.email && <p>{selectedExistingContact.email}</p>}
                        </div>

                        <h3 className={styles.sectionTitle}>
                            <FaTags /> Assign Contact Types (Optional)
                        </h3>

                        <p className={styles.helpText}>
                            Select the contact types that apply to this contact for {clientName || 'this client'}.
                            You can skip this step and add types later.
                        </p>

                        {loadingContactTypes ? (
                            <div className={styles.loadingState}>
                                <div className={styles.spinner}></div>
                                Loading contact types...
                            </div>
                        ) : contactTypes.length === 0 ? (
                            <div className={styles.emptyState}>
                                No contact types available. You can add them in the Configurations page.
                            </div>
                        ) : (
                            <div className={styles.contactTypesGrid}>
                                {contactTypes.map(type => (
                                    <div
                                        key={type.contactTypeId}
                                        className={`${styles.contactTypeCard} ${
                                            selectedContactTypes.includes(type.contactTypeId)
                                                ? styles.selected
                                                : ''
                                        }`}
                                        onClick={() => handleContactTypeToggle(type.contactTypeId)}
                                    >
                                        <div className={styles.contactTypeCheck}>
                                            {selectedContactTypes.includes(type.contactTypeId) && (
                                                <FaCheckCircle />
                                            )}
                                        </div>
                                        <div className={styles.contactTypeInfo}>
                                            <h4 className={styles.contactTypeName}>{type.contactType}</h4>
                                            {type.description && (
                                                <p className={styles.contactTypeDescription}>
                                                    {type.description}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {selectedContactTypes.length > 0 && (
                            <div className={styles.selectedCount}>
                                {selectedContactTypes.length} contact type
                                {selectedContactTypes.length !== 1 ? 's' : ''} selected
                            </div>
                        )}

                        <div className={styles.formActions}>
                            <button
                                type="button"
                                className={styles.backButton}
                                onClick={() => setMode('search')}
                                disabled={saving}
                            >
                                ← Back
                            </button>
                            <button
                                type="button"
                                className={styles.submitButton}
                                onClick={handleAssignContactTypes}
                                disabled={saving}
                            >
                                {saving ? (
                                    <>
                                        <span className={styles.spinner}></span>
                                        Linking Contact...
                                    </>
                                ) : (
                                    <>
                                        <FaPlus /> Link Contact
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                )}

                {/* Create Mode: Step 1 - Contact Information */}
                {mode === 'create' && step === 1 && (
                    <form onSubmit={handleSubmit} className={styles.form}>
                        {/* Step Indicator */}
                        <div className={styles.stepIndicator}>
                            <div className={`${styles.step} ${styles.active}`}>
                                <div className={styles.stepNumber}>1</div>
                                <div className={styles.stepLabel}>Contact Info</div>
                            </div>
                            <div className={styles.stepLine}></div>
                            <div className={styles.step}>
                                <div className={styles.stepNumber}>2</div>
                                <div className={styles.stepLabel}>Contact Types</div>
                            </div>
                        </div>

                        <div className={styles.formStep}>
                            <h3 className={styles.sectionTitle}>
                                <FaUser /> Contact Information
                            </h3>

                            {/* First Name and Last Name */}
                            <div className={styles.formRow}>
                                <div className={styles.formGroup}>
                                    <label htmlFor="firstName" className={styles.label}>
                                        First Name <span className={styles.required}>*</span>
                                    </label>
                                    <input
                                        type="text"
                                        id="firstName"
                                        name="firstName"
                                        className={`${styles.input} ${errors.firstName ? styles.inputError : ''}`}
                                        value={formData.firstName}
                                        onChange={handleChange}
                                        maxLength={50}
                                        disabled={saving}
                                        required
                                    />
                                    {errors.firstName && (
                                        <span className={styles.errorMessage}>{errors.firstName}</span>
                                    )}
                                </div>

                                <div className={styles.formGroup}>
                                    <label htmlFor="lastName" className={styles.label}>
                                        Last Name
                                    </label>
                                    <input
                                        type="text"
                                        id="lastName"
                                        name="lastName"
                                        className={`${styles.input} ${errors.lastName ? styles.inputError : ''}`}
                                        value={formData.lastName}
                                        onChange={handleChange}
                                        maxLength={50}
                                        disabled={saving}
                                    />
                                    {errors.lastName && (
                                        <span className={styles.errorMessage}>{errors.lastName}</span>
                                    )}
                                </div>
                            </div>

                            {/* Email */}
                            <div className={styles.formGroup}>
                                <label htmlFor="email" className={styles.label}>
                                    <FaEnvelope /> Email
                                </label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    className={`${styles.input} ${errors.email ? styles.inputError : ''}`}
                                    value={formData.email}
                                    onChange={handleChange}
                                    maxLength={254}
                                    disabled={saving}
                                    placeholder="contact@example.com"
                                />
                                {errors.email && (
                                    <span className={styles.errorMessage}>{errors.email}</span>
                                )}
                            </div>

                            {/* Address Section */}
                            <h4 className={styles.subsectionTitle}>
                                <FaMapMarkerAlt /> Address (Optional)
                            </h4>

                            {/* Address Line 1 */}
                            <div className={styles.formGroup}>
                                <label htmlFor="address1" className={styles.label}>
                                    Address Line 1
                                </label>
                                <input
                                    type="text"
                                    id="address1"
                                    name="address1"
                                    className={`${styles.input} ${errors.address1 ? styles.inputError : ''}`}
                                    value={formData.address1}
                                    onChange={handleChange}
                                    maxLength={100}
                                    disabled={saving}
                                    placeholder="123 Main Street"
                                />
                                {errors.address1 && (
                                    <span className={styles.errorMessage}>{errors.address1}</span>
                                )}
                            </div>

                            {/* Address Line 2 */}
                            <div className={styles.formGroup}>
                                <label htmlFor="address2" className={styles.label}>
                                    Address Line 2
                                </label>
                                <input
                                    type="text"
                                    id="address2"
                                    name="address2"
                                    className={`${styles.input} ${errors.address2 ? styles.inputError : ''}`}
                                    value={formData.address2}
                                    onChange={handleChange}
                                    maxLength={100}
                                    disabled={saving}
                                    placeholder="Apt 4B, Suite 200"
                                />
                                {errors.address2 && (
                                    <span className={styles.errorMessage}>{errors.address2}</span>
                                )}
                            </div>

                            {/* City, State, ZIP */}
                            <div className={styles.formRow}>
                                <div className={styles.formGroup}>
                                    <label htmlFor="city" className={styles.label}>
                                        City
                                    </label>
                                    <input
                                        type="text"
                                        id="city"
                                        name="city"
                                        className={`${styles.input} ${errors.city ? styles.inputError : ''}`}
                                        value={formData.city}
                                        onChange={handleChange}
                                        maxLength={100}
                                        disabled={saving}
                                        placeholder="New York"
                                    />
                                    {errors.city && (
                                        <span className={styles.errorMessage}>{errors.city}</span>
                                    )}
                                </div>

                                <div className={styles.formGroup}>
                                    <label htmlFor="stateCode" className={styles.label}>
                                        State
                                    </label>
                                    <select
                                        id="stateCode"
                                        name="stateCode"
                                        className={`${styles.select} ${errors.stateCode ? styles.inputError : ''}`}
                                        value={formData.stateCode}
                                        onChange={handleChange}
                                        disabled={saving}
                                    >
                                        <option value="">Select State</option>
                                        {US_STATES.map(state => (
                                            <option key={state.code} value={state.code}>
                                                {state.code} - {state.name}
                                            </option>
                                        ))}
                                    </select>
                                    {errors.stateCode && (
                                        <span className={styles.errorMessage}>{errors.stateCode}</span>
                                    )}
                                </div>

                                <div className={styles.formGroup}>
                                    <label htmlFor="zip" className={styles.label}>
                                        ZIP Code
                                    </label>
                                    <input
                                        type="text"
                                        id="zip"
                                        name="zip"
                                        className={`${styles.input} ${errors.zip ? styles.inputError : ''}`}
                                        value={formData.zip}
                                        onChange={handleChange}
                                        maxLength={20}
                                        disabled={saving}
                                        placeholder="10001"
                                    />
                                    {errors.zip && (
                                        <span className={styles.errorMessage}>{errors.zip}</span>
                                    )}
                                </div>
                            </div>

                            {/* Status Options */}
                            <div className={styles.checkboxGroup}>
                                <label className={styles.checkboxLabel}>
                                    <input
                                        type="checkbox"
                                        name="isSubscribed"
                                        checked={formData.isSubscribed}
                                        onChange={handleChange}
                                        disabled={saving}
                                    />
                                    <span>Subscribed to communications</span>
                                </label>

                                <label className={styles.checkboxLabel}>
                                    <input
                                        type="checkbox"
                                        name="clientPortalAccess"
                                        checked={formData.clientPortalAccess}
                                        onChange={handleChange}
                                        disabled={saving}
                                    />
                                    <span>Client Portal Access</span>
                                </label>

                                <label className={styles.checkboxLabel}>
                                    <input
                                        type="checkbox"
                                        name="isActive"
                                        checked={formData.isActive}
                                        onChange={handleChange}
                                        disabled={saving}
                                    />
                                    <span>Active</span>
                                </label>
                            </div>
                        </div>

                        {/* Form Actions */}
                        <div className={styles.formActions}>
                            <button
                                type="button"
                                className={styles.cancelButton}
                                onClick={() => setMode('search')}
                                disabled={saving}
                            >
                                ← Back to Search
                            </button>
                            <button
                                type="button"
                                className={styles.nextButton}
                                onClick={handleNext}
                                disabled={saving}
                            >
                                Next: Contact Types →
                            </button>
                        </div>
                    </form>
                )}

                {/* Create Mode: Step 2 - Contact Types */}
                {mode === 'create' && step === 2 && (
                    <form onSubmit={handleSubmit} className={styles.form}>
                        {/* Step Indicator */}
                        <div className={styles.stepIndicator}>
                            <div className={styles.step}>
                                <div className={styles.stepNumber}>1</div>
                                <div className={styles.stepLabel}>Contact Info</div>
                            </div>
                            <div className={styles.stepLine}></div>
                            <div className={`${styles.step} ${styles.active}`}>
                                <div className={styles.stepNumber}>2</div>
                                <div className={styles.stepLabel}>Contact Types</div>
                            </div>
                        </div>

                        <div className={styles.formStep}>
                            <h3 className={styles.sectionTitle}>
                                <FaTags /> Assign Contact Types (Optional)
                            </h3>

                            <p className={styles.helpText}>
                                Select the contact types that apply to this contact for {clientName || 'this client'}.
                                You can skip this step and add types later.
                            </p>

                            {loadingContactTypes ? (
                                <div className={styles.loadingState}>
                                    <div className={styles.spinner}></div>
                                    Loading contact types...
                                </div>
                            ) : contactTypes.length === 0 ? (
                                <div className={styles.emptyState}>
                                    No contact types available. You can add them in the Configurations page.
                                </div>
                            ) : (
                                <div className={styles.contactTypesGrid}>
                                    {contactTypes.map(type => (
                                        <div
                                            key={type.contactTypeId}
                                            className={`${styles.contactTypeCard} ${
                                                selectedContactTypes.includes(type.contactTypeId)
                                                    ? styles.selected
                                                    : ''
                                            }`}
                                            onClick={() => handleContactTypeToggle(type.contactTypeId)}
                                        >
                                            <div className={styles.contactTypeCheck}>
                                                {selectedContactTypes.includes(type.contactTypeId) && (
                                                    <FaCheckCircle />
                                                )}
                                            </div>
                                            <div className={styles.contactTypeInfo}>
                                                <h4 className={styles.contactTypeName}>{type.contactType}</h4>
                                                {type.description && (
                                                    <p className={styles.contactTypeDescription}>
                                                        {type.description}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {selectedContactTypes.length > 0 && (
                                <div className={styles.selectedCount}>
                                    {selectedContactTypes.length} contact type
                                    {selectedContactTypes.length !== 1 ? 's' : ''} selected
                                </div>
                            )}
                        </div>

                        {/* Form Actions */}
                        <div className={styles.formActions}>
                            <button
                                type="button"
                                className={styles.backButton}
                                onClick={handlePrevious}
                                disabled={saving}
                            >
                                ← Back
                            </button>
                            <button
                                type="submit"
                                className={styles.submitButton}
                                disabled={saving}
                            >
                                {saving ? (
                                    <>
                                        <span className={styles.spinner}></span>
                                        Creating Contact...
                                    </>
                                ) : (
                                    <>
                                        <FaPlus /> Create Contact
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default AddContactModal;
